import React from 'react';
import { observer } from 'mobx-react';
import { CircularProgress } from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import {
    ThemedButton,
    FlexDiv,
    SpaceBetweenDiv,
    CheckboxInput,
} from '@awarego-ui/components';
import TableClient from '../../../components/table/TableClient';
import ErrorMessage from '../../../components/errormessage';
import RequireRole from '../../RequireRole';

function BouncedEmailsTable({ companyId, productId, store }) {
    const {
        columns,
        usersList,
        isDownloading,
        loadingUsersList,
        showObsolete,
        bouncedObsoleteCount,
        error,
        selectedBouncedCount,
        resendingBounced,
        removingBounced,
        selection,
    } = store;

    const data = loadingUsersList ? [] : usersList.slice();
    const total = usersList.length;
    const allChecked = total > 0 && selectedBouncedCount === total;
    const someChecked =
        selectedBouncedCount > 0 && selectedBouncedCount < total;
    const busy = loadingUsersList || resendingBounced || removingBounced;
    const productLabel = store.automationStore ? 'training' : 'assessment';

    const handleDownloadCSV = () => {
        store.downloadCSV(companyId, productId);
    };

    const handleSelectAll = (event) => {
        if (event.target.checked) selection.setAllPagesSelected();
        else selection.clearSelection();
    };

    const handleResend = () => {
        store.commonStore.showConfirm(
            `Resend email to ${selectedBouncedCount} selected ${
                selectedBouncedCount === 1 ? 'user' : 'users'
            }?`,
            'Resend',
            'Resend bounced emails',
            () => store.resendBounced(companyId, productId)
        );
    };

    const handleRemove = () => {
        store.commonStore.showConfirm(
            `Remove ${selectedBouncedCount} selected ${
                selectedBouncedCount === 1 ? 'user' : 'users'
            } from this ${productLabel}'s lists? They will no longer be enrolled.`,
            'Remove users',
            'Remove bounced users',
            () => store.removeBounced(companyId, productId),
            true
        );
    };

    return (
        <FlexDiv column>
            {error && <ErrorMessage error={error} />}
            <SpaceBetweenDiv my={10} alignCenter>
                <FlexDiv alignCenter gap={16}>
                    {bouncedObsoleteCount > 0 || showObsolete ? (
                        <CheckboxInput
                            label={`Show obsolete (${bouncedObsoleteCount})`}
                            checked={showObsolete}
                            onChange={() =>
                                store.setShowObsolete(!showObsolete)
                            }
                        />
                    ) : (
                        <span />
                    )}
                    {!loadingUsersList && total > 0 && (
                        <CheckboxInput
                            label={
                                selectedBouncedCount > 0 &&
                                selectedBouncedCount !== total
                                    ? `Selected (${selectedBouncedCount}/${total})`
                                    : `Select all (${total})`
                            }
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={handleSelectAll}
                        />
                    )}
                </FlexDiv>
                <FlexDiv gap={8} alignCenter>
                    <RequireRole>
                        <ThemedButton
                            mode="secondary"
                            size="tiny"
                            $noTextTransform
                            onClick={handleResend}
                            disabled={busy || selectedBouncedCount === 0}
                            text={
                                <>
                                    {resendingBounced && (
                                        <CircularProgress size={10} />
                                    )}{' '}
                                    Resend
                                </>
                            }
                        />
                        <ThemedButton
                            mode="secondary"
                            size="tiny"
                            $noTextTransform
                            onClick={handleRemove}
                            disabled={busy || selectedBouncedCount === 0}
                            text={
                                <>
                                    {removingBounced && (
                                        <CircularProgress size={10} />
                                    )}{' '}
                                    Remove users
                                </>
                            }
                        />
                    </RequireRole>
                    <ThemedButton
                        mode="secondary"
                        size="tiny"
                        $noTextTransform
                        onClick={handleDownloadCSV}
                        disabled={loadingUsersList || usersList.length === 0}
                        text={
                            <>
                                {isDownloading && (
                                    <CircularProgress size={10} />
                                )}{' '}
                                Download .csv
                                <GetAppIcon fontSize="small" />
                            </>
                        }
                    />
                </FlexDiv>
            </SpaceBetweenDiv>
            <TableClient
                columns={columns}
                data={data}
                defaultSortBy={'name'}
                defaultSortDescent={false}
                loadingData={loadingUsersList}
                getRowId={(row) => row.email}
            />
        </FlexDiv>
    );
}

export default observer(BouncedEmailsTable);
